import { useEffect, useRef, useState } from 'react';

/**
 * Reusable MapView. The same component drives the full /map page and
 * embedded use-cases like the coordinate picker modal on /nodes.
 *
 * Props:
 *   mode               'full' | 'picker'              default 'full'
 *   initialCenter      [lon, lat]                     default Kyiv
 *   initialZoom        number                         default 5.5
 *   initialPitch       number                         default mode==='picker' ? 0 : 45
 *   onReady            (map, maplibregl) => void      called after style.load
 *   onError            (err) => void
 *   className, style   passthrough on the container div
 *
 * Vendor JS lives in /runtime-assets/vendor/maplibre/* and is loaded via
 * plain <script>/<link> tags — these files are not ES modules.
 */

// Vendor widgets read these globals. Stub unconditionally so vendor JS loads
// cleanly even if features aren't fully wired (fixes broken StyleSwitcher
// dropdown rendering when copRenderStyle is undefined).
if (typeof window !== 'undefined') {
  if (!window.copRenderStyle) window.copRenderStyle = {};
  if (!window.__copDisplayTz) window.__copDisplayTz = 'UTC';
}

const MAPLIBRE_BASE = '/runtime-assets/vendor/maplibre';

const MAPLIBRE_CSS_ID     = 'maplibre-gl-css';
const MAPLIBRE_SCRIPT_ID  = 'maplibre-gl-script';
const MAPLIBRE_CSS_URL    = `${MAPLIBRE_BASE}/maplibre-gl.css`;
const MAPLIBRE_SCRIPT_URL = `${MAPLIBRE_BASE}/maplibre-gl.js`;

const FLYTO_SCRIPT_ID  = 'app-flyto-script';
const FLYTO_SCRIPT_URL = `${MAPLIBRE_BASE}/flyTo/app.flyTo.js`;
const LABEL_SCRIPT_ID  = 'app-marker-label-script';
const LABEL_SCRIPT_URL = `${MAPLIBRE_BASE}/label/app.label.js`;
// Auto-fit camera plugin — listens at MapLibre layer for GeoJSON source
// growth + maplibregl.Marker bursts and animates the camera to fit the
// new content's bbox. Centralises the «каждый виджет сам пишет flyTo»
// anti-pattern. Depends on app.flyTo.js (loaded first); attached once
// inside map.on('load') BEFORE the widget controls fire their initial
// fetch — see manuals/cop-autofit-plugin.md §10.2.
const COP_AUTOFIT_SCRIPT_ID  = 'app-cop-autofit-script';
const COP_AUTOFIT_SCRIPT_URL = `${MAPLIBRE_BASE}/cop/app.cop-autofit.js`;
// 2026-05-27 round 51 — 3D air-track plugin. Renders the orange
// aerial line above the ground COP-Layers trail for FF Locations
// / NFFI tracks using MapLibre's CustomLayerInterface + Three.js.
// ES-module → must be loaded with type="module" so `import * as
// THREE from "three"` resolves via the importmap in index.html
// (mapped to /runtime-assets/vendor/three@0.183.2/three.module.js).
// Picks up cop-layers' state via prototype monkey-patch — no
// fetch / no copy of the items array. Full design in
// manuals/cop-tracks-plugin.md.
const COP_TRACKS_SCRIPT_ID  = 'app-cop-tracks-script';
const COP_TRACKS_SCRIPT_URL = `${MAPLIBRE_BASE}/cop/app.cop-tracks.js`;
const COP_CANVAS_SCRIPT_ID  = 'app-cop-canvas-script';
const COP_CANVAS_SCRIPT_URL = `${MAPLIBRE_BASE}/cop/app.cop.canvas.js`;
// Atmospheric sky helper — toggles MapLibre's `map.setSky({…})`
// blue-sky/horizon-fog stack on z>6 and clears it when zoomed out
// past z6 so the void around the globe stays transparent — letting
// the starry-sky background image painted under the map container
// show through. Mirrors briar-android/.../app.sky.js verbatim.
const SKY_SCRIPT_ID  = 'app-sky-script';
const SKY_SCRIPT_URL = `${MAPLIBRE_BASE}/controls/app.sky.js`;
// Starry-sky background painted under the map container. MapLibre's
// canvas is transparent in the area around the globe (when the
// projection is globe and the operator zooms out enough to see the
// edge of the world), so this image fills that void.
const STARRY_SKY_BG_URL = '/runtime-assets/images/gEdfUOnqJFOUYizc9FGQjtBqvuatNgqt.jpg';
// Classifier-cascade renderStyle resolver. Reads
// data/classifier/{polyline,polygon}-styles.json + applies the
// affiliation → status → mainIconStyles[typeId] → overrides cascade
// (per manuals/mobile-polyline-polygon-styling.md §2) to every
// polyline/polygon object's feature.properties as __* keys. Without
// it the COP Layers widget's enrichFeature() falls back to bare
// affiliation colours — no dash patterns, no fills, no decorators.
const COP_STYLE_SCRIPT_ID  = 'app-cop-style-script';
const COP_STYLE_SCRIPT_URL = `${MAPLIBRE_BASE}/cop/app.cop.style.js`;

// app.cop.css ships with the COP MapLibre plugin and styles the
// .app-popup-body / -title / -key / -val / -divider classes that
// app.cop-layers.js builds at popup-render time. The plugin's own
// header comment says "requires app.cop.css loaded alongside" — but
// loadMapLibreAssets used to skip it, so the popup classes had NO
// styling at all and operators saw dim text on the MapLibre default
// white bg. Hot-fix: load it explicitly via the same ensureLink path
// every other vendor stylesheet uses.
const COP_CSS_ID  = 'app-cop-css';
const COP_CSS_URL = `${MAPLIBRE_BASE}/cop/app.cop.css`;

const CONTROLS_BASE = `${MAPLIBRE_BASE}/controls/`;
const AWDS_CSS_ID  = 'app-awds-css';
const AWDS_CSS_URL = CONTROLS_BASE + 'app.awds.css';
const COP_LAYERS_CSS_ID  = 'app-cop-layers-css';
const COP_LAYERS_CSS_URL = CONTROLS_BASE + 'app.cop-layers.css';
// 2026-06-09 — Air-targets widget (passive renderer). See
// manuals/NODE-targets/PLAN.md and vendor/maplibre/controls/app.targets.js.
const TARGETS_CSS_ID  = 'app-targets-css';
const TARGETS_CSS_URL = CONTROLS_BASE + 'app.targets.css';
// SSE endpoint exposed by ad-tms-service through nginx (same-origin
// /api/targets-feed/stream → 127.0.0.1:6537/api/local/targets/stream).
// The widget is a passive renderer — we own the EventSource lifecycle
// here so the widget doesn't have to know about transport details.
const TARGETS_STREAM_URL = '/api/targets-feed/stream';
const STYLE_SWITCHER_CSS_ID  = 'app-styleswitcher-css';
const STYLE_SWITCHER_CSS_URL = CONTROLS_BASE + 'app.styleswitcher.css';
const TMS_LAYERS_CSS_ID  = 'app-tms-layers-css';
const TMS_LAYERS_CSS_URL = CONTROLS_BASE + 'app.tms-layers.css';
// ─── Android-parity right-stack stylesheets (2026-05-19) ────────────
// Each of these plugins ships its own inline popdown / panel styling;
// without the CSS the panels would render as unstyled white blocks.
const GEOCODER_CSS_ID       = 'app-geocoder-css';
const GEOCODER_CSS_URL      = CONTROLS_BASE + 'app.geocoder.css';
const MAP_LANGUAGE_CSS_ID   = 'app-map-language-css';
const MAP_LANGUAGE_CSS_URL  = CONTROLS_BASE + 'app.map-language.css';
const COORDS_INFO_CSS_ID    = 'app-coords-info-css';
const COORDS_INFO_CSS_URL   = CONTROLS_BASE + 'app.coords-info.css';
// Minimap toggle — bottom-right miniature overview map (320×180)
// driven by the mapbox-gl-control-minimap UMD plugin. The .css
// styles only the outer frame; the inner miniature canvas is drawn
// by the library itself.
const MINIMAP_CSS_ID        = 'app-minimap-css';
const MINIMAP_CSS_URL       = CONTROLS_BASE + 'app.minimap.css';
// ─── Global button polish — must load after every per-control CSS
//     above so its !important rules win over plugin defaults
//     (icon centering + compass active-state highlight). ─
const TMS_BUTTONS_CSS_ID  = 'app-tms-buttons-css';
const TMS_BUTTONS_CSS_URL = CONTROLS_BASE + 'app.tms-buttons.css';
// Marker Labels plugin stylesheet. The rules used to live inside
// app.tms-layers.css because TMS Layers always shipped together with
// app.label.js on the coordinator. Broken out into its own file so
// mobile (which doesn't load the TMS Layers widget) still gets the
// label styling — same file referenced via <link> in mobile's
// tms-map.html. Without it `.app-marker-label` divs render as
// unstyled invisible boxes.
const LABEL_CSS_ID  = 'app-marker-label-css';
const LABEL_CSS_URL = `${MAPLIBRE_BASE}/label/app.label.css`;

const CONTROL_SCRIPTS = [
  { id: 'app-pitch-ctrl',         url: CONTROLS_BASE + 'app.pitch.js' },
  { id: 'app-rotate-ctrl',        url: CONTROLS_BASE + 'app.rotate.js' },
  { id: 'app-styleswitcher-ctrl', url: CONTROLS_BASE + 'app.styleswitcher.js' },
  // ─── 2026-05-19 — Android-parity right-stack additions ────────────
  // app.proj4.min.js loads first because app.coords-info.js's
  // UTM / SK-42 projection setup calls `proj4.defs(...)` at top-level
  // of its ProjectionConverter helper. Without proj4 available the
  // CoordsInfoControl onAdd throws and the button never appears.
  { id: 'app-proj4',              url: `${MAPLIBRE_BASE}/proj4.min.js` },
  // Self-contained desktop originals from /Sandbox/old-client (clean
  // — no mobile UI tweaks). Both daynight + grid are pure
  // map-layer machinery + a FontAwesome glyph button, no external
  // DOM panel needed; coord already loads FA Pro 7 so the icons
  // render natively.
  { id: 'app-daynight-ctrl',      url: CONTROLS_BASE + 'app.daynight.js' },
  { id: 'app-grid-ctrl',          url: CONTROLS_BASE + 'app.grid.js' },
  // Geocoder + MapLanguage + CoordsInfo — Android-vendored versions
  // because they bundle their own inline popdown panels (the
  // sandbox originals require `<div id="geocoder-panel">` etc in
  // index.html, which our React SPA doesn't have). Self-contained
  // ports = drop-in here, identical look to the Android client.
  { id: 'app-geocoder-ctrl',      url: CONTROLS_BASE + 'app.geocoder.js' },
  { id: 'app-map-language-ctrl',  url: CONTROLS_BASE + 'app.map-language.js' },
  { id: 'app-coords-info-ctrl',   url: CONTROLS_BASE + 'app.coords-info.js' },
  // Minimap toggle (right-stack penultimate position) — UMD lib has
  // to load BEFORE app.minimap.js because the latter reads
  // `maplibregl.Minimap` at construction time. The UMD plugin
  // registers itself on `window.mapboxgl`; app.minimap.js's
  // ensureMapboxGlAlias() aliases that to window.maplibregl.
  // UMD lives in its own vendor dir per the no-CDN / per-package
  // layout convention — see CLAUDE.md "Vendoring policy".
  { id: 'app-minimap-umd',        url: `${MAPLIBRE_BASE}/mapbox-gl-control-minimap/mapboxgl-control-minimap.js` },
  { id: 'app-minimap-ctrl',       url: CONTROLS_BASE + 'app.minimap.js' },
  // ─── widgets (top-left, optional) ────────────────────────────────
  { id: 'app-awds-ctrl',          url: CONTROLS_BASE + 'app.awds.js' },
  { id: 'app-cop-layers-ctrl',    url: CONTROLS_BASE + 'app.cop-layers.js' },
  { id: 'app-tms-layers-ctrl',    url: CONTROLS_BASE + 'app.tms-layers.js' },
  // 2026-06-09 — Air-targets widget. Passive renderer. The SSE
  // subscription lives in the map-view useEffect (see below).
  { id: 'app-targets-ctrl',       url: CONTROLS_BASE + 'app.targets.js' },
];

const DEFAULT_STYLE_ID = 'liberty';

function buildStyleUrl(styleId, apiKey) {
  const key = apiKey ? `?key=${encodeURIComponent(apiKey)}` : '';
  return `https://mvt.micro-gis.com/styles/${styleId}${key}`;
}

// ─── Cursor lat/lon HUD control ────────────────────────────────────────────
// Renders a tiny crosshair (Material `my_location`) followed by the live
// lat/lon string. Replaces the previous "Cursor: <coords>" text prefix —
// the icon takes ~12 px vs the word's ~36 px, freeing horizontal space in
// the bottom-left strip (relevant on the mobile WebView where the strip
// can spill off-screen, and visually tightens the desktop too).
const CURSOR_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" ' +
  'style="vertical-align:middle;margin-right:5px;flex-shrink:0;" ' +
  'xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3' +
  'A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06' +
  'A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06z' +
  'M12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>' +
  '</svg>';
function CursorCoordinatesControl() {}
CursorCoordinatesControl.prototype.onAdd = function () {
  this._container = document.createElement('div');
  this._container.className = 'maplibregl-ctrl';
  this._container.style.cssText =
    'background:rgba(30,30,30,0.8);color:#4ade80;font:11px/1.4 monospace;' +
    'padding:3px 8px;border-radius:4px;white-space:nowrap;pointer-events:none;' +
    'margin:0;order:2;display:inline-flex;align-items:center';
  const label = document.createElement('span');
  label.textContent = 'n/a';
  this._container.innerHTML = CURSOR_ICON_SVG;
  this._container.appendChild(label);
  this._label = label;
  return this._container;
};
CursorCoordinatesControl.prototype.onRemove = function () {
  this._container?.parentNode?.removeChild(this._container);
};
CursorCoordinatesControl.prototype.setValue = function (value) {
  if (this._label) this._label.textContent = value || 'n/a';
};

// ─── Canvas-badge text-to-icon override ────────────────────────────────────
// Vendored `app.cop.canvas.js` (CopCanvasRenderer._createDebugBadge) appends
// a small green pill with textContent "Canvas ✓" to .maplibregl-ctrl-bottom-
// left. We swap the word "Canvas" for an inline `brush` SVG so the strip
// fits portrait phone screens without spilling off-edge. Implemented as a
// MutationObserver here (in OUR JS) rather than as a direct edit of the
// vendor file: per Lesson #30 in CLAUDE.md, vendored assets must be
// override-patched in our own code so they survive re-sync from COP ADMS.
// MUST be installed BEFORE `new CopCanvasRenderer(...)` runs, because the
// renderer's constructor synchronously appends the badge — if the observer
// is set up after, we miss the addedNodes mutation and have to fall back
// to a manual sweep of existing children (we do both, defensively).
const CANVAS_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" ' +
  'style="vertical-align:middle;margin-right:5px;flex-shrink:0;" ' +
  'xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 ' +
  '2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37l-1.34-1.34a.9959.9959 0 ' +
  '0 0-1.41 0L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.02 0-1.41z"/>' +
  '</svg>';
const CANVAS_BADGE_TEXT = 'Canvas ✓';
function setupCanvasBadgeSwap(map) {
  const container = map.getContainer().querySelector('.maplibregl-ctrl-bottom-left');
  if (!container) return null;
  function applySwap(node) {
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.tmsCanvasSwapped === '1') return;
    if (node.textContent !== CANVAS_BADGE_TEXT) return;
    node.style.display = 'inline-flex';
    node.style.alignItems = 'center';
    node.innerHTML = CANVAS_ICON_SVG + '✓';
    node.dataset.tmsCanvasSwapped = '1';
  }
  Array.from(container.children).forEach(applySwap);
  const mo = new MutationObserver((muts) => {
    muts.forEach((m) => m.addedNodes.forEach(applySwap));
  });
  mo.observe(container, { childList: true });
  return mo;
}

// ─── Asset loaders ─────────────────────────────────────────────────────────
function ensureLink(id, href) {
  if (document.getElementById(id)) return;
  const l = document.createElement('link');
  l.id = id; l.rel = 'stylesheet'; l.href = href;
  document.head.appendChild(l);
}

function loadMapLibreAssets() {
  return new Promise((resolve, reject) => {
    ensureLink(MAPLIBRE_CSS_ID,     MAPLIBRE_CSS_URL);
    ensureLink(AWDS_CSS_ID,         AWDS_CSS_URL);
    ensureLink(COP_CSS_ID,          COP_CSS_URL);
    ensureLink(COP_LAYERS_CSS_ID,   COP_LAYERS_CSS_URL);
    ensureLink(TARGETS_CSS_ID,      TARGETS_CSS_URL);
    ensureLink(STYLE_SWITCHER_CSS_ID, STYLE_SWITCHER_CSS_URL);
    ensureLink(TMS_LAYERS_CSS_ID,   TMS_LAYERS_CSS_URL);
    ensureLink(LABEL_CSS_ID,        LABEL_CSS_URL);
    ensureLink(GEOCODER_CSS_ID,     GEOCODER_CSS_URL);
    ensureLink(MAP_LANGUAGE_CSS_ID, MAP_LANGUAGE_CSS_URL);
    ensureLink(COORDS_INFO_CSS_ID,  COORDS_INFO_CSS_URL);
    ensureLink(MINIMAP_CSS_ID,      MINIMAP_CSS_URL);
    ensureLink(TMS_BUTTONS_CSS_ID,  TMS_BUTTONS_CSS_URL);

    const finish = () => {
      if (window.maplibregl) resolve(window.maplibregl);
      else reject(new Error('MapLibre GL failed to initialize.'));
    };
    if (window.maplibregl) { finish(); return; }
    const existing = document.getElementById(MAPLIBRE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error',
        () => reject(new Error('Failed to load MapLibre script.')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = MAPLIBRE_SCRIPT_ID; s.src = MAPLIBRE_SCRIPT_URL; s.async = true;
    s.onload = finish;
    s.onerror = () => reject(new Error('Failed to load MapLibre script.'));
    document.body.appendChild(s);
  });
}

function loadSingleScript(id, url) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve();
      if (existing.dataset.loaded === 'error') return reject(new Error('Failed to load ' + id));
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load ' + id)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = id; s.src = url; s.async = true;
    s.onload  = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = () => { s.dataset.loaded = 'error'; reject(new Error('Failed to load ' + id)); };
    document.body.appendChild(s);
  });
}

function loadLabelScript() {
  return loadSingleScript(FLYTO_SCRIPT_ID, FLYTO_SCRIPT_URL)
    .then(() => loadSingleScript(LABEL_SCRIPT_ID, LABEL_SCRIPT_URL));
}

// COP auto-fit camera plugin. Chains flyTo → cop-autofit (autofit's
// single-feature path calls App.flyTo for the smooth fly-in). flyTo
// itself is also loaded by loadLabelScript() — the inner dedupe in
// loadSingleScript ensures the script tag is created only once. Non-
// fatal on failure: missing autofit just means no centralised auto-fit
// (each widget keeps its own ad-hoc camera moves, as before).
function loadCopAutofitScript() {
  return loadSingleScript(FLYTO_SCRIPT_ID, FLYTO_SCRIPT_URL)
    .then(() => loadSingleScript(COP_AUTOFIT_SCRIPT_ID, COP_AUTOFIT_SCRIPT_URL))
    .catch(() => {});
}

// COP-tracks 3D air-track plugin. Unlike every other vendor script
// loaded here, this one is an ES module — Three.js is pulled via
// `import * as THREE from "three"`, mapped to the local vendor copy
// by the importmap in index.html. Loader uses `type="module"`; all
// other shape mirrors loadSingleScript. Non-fatal on failure: the
// ground COP-layer trail keeps working without the air projection.
function loadCopTracksScript() {
  return new Promise((resolve) => {
    const existing = document.getElementById(COP_TRACKS_SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve();
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = COP_TRACKS_SCRIPT_ID;
    s.type = 'module';   // REQUIRED — plugin is ES module, see PORTING.md §3
    s.src = COP_TRACKS_SCRIPT_URL;
    s.onload  = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = () => { s.dataset.loaded = '1'; resolve(); };
    document.body.appendChild(s);
  });
}

function loadCopCanvasScript() {
  return new Promise((resolve) => {
    if (window.CopCanvasRenderer) return resolve();
    const existing = document.getElementById(COP_CANVAS_SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve();
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = COP_CANVAS_SCRIPT_ID; s.src = COP_CANVAS_SCRIPT_URL; s.async = true;
    s.onload  = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = () => { s.dataset.loaded = '1'; resolve(); };
    document.body.appendChild(s);
  });
}

function loadCopStyleScript() {
  // Style cascade resolver — must load BEFORE app.cop-layers.js so
  // window.copRenderStyle.applyStylesToObjects is defined when the
  // widget asks for it. The styles JSONs live under
  // /runtime-assets/data/classifier/ (shipped with the public
  // build). Override window.COP_STYLE_BASE before the script loads
  // if you need a different location.
  if (typeof window !== 'undefined' && !window.COP_STYLE_BASE) {
    window.COP_STYLE_BASE = '/runtime-assets/data/classifier';
  }
  return loadSingleScript(COP_STYLE_SCRIPT_ID, COP_STYLE_SCRIPT_URL)
      .catch(() => {});   // non-fatal — widget falls back to enrichFeature
}

function loadControlScripts() {
  return Promise.all(
    CONTROL_SCRIPTS.map(({ id, url }) => new Promise((resolve) => {
      const existing = document.getElementById(id);
      if (existing) {
        if (existing.dataset.loaded === '1') return resolve();
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', () => resolve(), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.id = id; s.src = url; s.async = true;
      s.onload  = () => { s.dataset.loaded = '1'; resolve(); };
      s.onerror = () => { s.dataset.loaded = '1'; resolve(); };
      document.body.appendChild(s);
    })),
  );
}

// ─── Component ─────────────────────────────────────────────────────────────
export function MapView({
  mode = 'full',
  initialCenter = [30.5234, 50.4501],
  initialZoom = 5.5,
  initialPitch,
  onReady,
  onError,
  className,
  style,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const cursorControlRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Resize observer — MapLibre needs an explicit resize() when its container
  // changes dimensions (we render inside flex shells and modal dialogs).
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const ro = new ResizeObserver(() => {
      if (mapRef.current) { try { mapRef.current.resize(); } catch { /* ignore */ } }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let disposed = false;
    // 2026-06-09 — Air-targets SSE subscription. Lifted to the
    // effect scope so the cleanup return can close it on unmount
    // even if the EventSource was opened mid-mountMap. Only
    // opened in `mode === 'full'` — passive renderer (widget) is
    // not registered in 'picker' mode, so there's nothing
    // consuming the ticks.
    let targetsEs = null;

    async function mountMap() {
      if (!containerRef.current || mapRef.current) return;
      try {
        const maplibregl = await loadMapLibreAssets();

        // AWDS widget config (read by app.awds.js at module evaluation time).
        window.AWDS_WIDGET_CONFIG = {
          apiBase: '',
          endpoints: {
            layersList: '/api/awds/layers',
            layerById:  '/api/awds/layers/{layerId}',
          },
          icons: {
            basic:              '/runtime-assets/vendor/basic/{typeId}.svg',
            metocAtmospheric:   '/runtime-assets/symbol-layers/Appendices/METOC/Atmospheric/{code}.svg',
            metocOceanographic: '/runtime-assets/symbol-layers/Appendices/METOC/Oceanographic/{code}.svg',
          },
        };

        await Promise.all([
          loadLabelScript(),
          loadCopAutofitScript(),
          loadCopTracksScript(),
          loadCopCanvasScript(),
          loadCopStyleScript(),
          loadSingleScript(SKY_SCRIPT_ID, SKY_SCRIPT_URL),
          loadControlScripts(),
        ]);
        if (disposed || !containerRef.current) return;

        let cfg = {};
        try {
          const r = await fetch('/api/config', { credentials: 'same-origin', cache: 'no-store' });
          if (r.ok) cfg = await r.json();
        } catch { /* non-fatal */ }
        const mvtApiKey = cfg.mvtApiKey || '';
        window.__mvtApiKey = mvtApiKey;
        window.__copMvtKey = mvtApiKey;

        const map = new maplibregl.Map({
          container: containerRef.current,
          style: buildStyleUrl(DEFAULT_STYLE_ID, mvtApiKey),
          center: initialCenter,
          zoom: initialZoom,
          pitch: typeof initialPitch === 'number' ? initialPitch : (mode === 'picker' ? 0 : 45),
          maxPitch: 85,
          canvasContextAttributes: { antialias: true },
        });

        // ─── Auto-fit camera (cop-autofit plugin) ──────────────────────
        // Registered FIRST so the plugin sees subsequent layer activations
        // triggered by widget controls. MapLibre fires `load` handlers in
        // registration order; widgets like CopLayersControl + AwdsWeather
        // attach their own load listeners inside their onAdd(), so any
        // attach() call after addControl(widget) would miss the widget's
        // initial source population. See manuals/cop-autofit-plugin.md §10.2
        // and the «autofit attach is the first thing in the load handler»
        // note in the reference cop-map-tab.jsx wiring.
        map.on('load', () => {
          if (window.App && window.App.copAutoFit) {
            try { window.App.copAutoFit.attach(map); }
            catch { /* non-fatal — widget falls back to ad-hoc flyTo */ }
          }
          // Atmospheric sky — blue-sky + horizon-fog on z>6, cleared
          // on z≤6 so the starry-sky background under the container
          // shows through around the globe edge. Internal listeners
          // re-toggle on every zoom + style swap.
          if (typeof window.bootTmsSky === 'function') {
            try { window.bootTmsSky(map); }
            catch { /* non-fatal — globe just won't get a sky band */ }
          }
          // Collapse the bottom-right attribution panel. MapLibre's
          // AttributionControl can't be created collapsed (the
          // `compact` option requires the panel to grow into the
          // open state at some point), so we flip the CSS classes
          // post-init: add maplibregl-compact (the collapsed pill)
          // and remove maplibregl-compact-show (the expanded
          // state). Operator clicks the i-icon to reopen.
          try {
            const attrib = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
            if (attrib) {
              attrib.classList.add('maplibregl-compact');
              attrib.classList.remove('maplibregl-compact-show');
            }
          } catch { /* non-fatal */ }
        });


        // ─── Top-right ───────────────────────────────────────────────────
        // Roster matches Android (tms-map.html line 640-693) so the
        // operator sees an identical control stack on coord + mobile.
        // Order: Zoom → Compass → Fullscreen → Geolocate → Globe →
        // Terrain → StyleSwitcher → Pitch → Geocoder → MapLanguage →
        // DayNight → Grid → Rotate → CoordsInfo. The bottom-three
        // (Terrain, …) are added below after the DEM-source setup.
        map.addControl(new maplibregl.NavigationControl({ showZoom: true,  showCompass: false }), 'top-right');
        map.addControl(new maplibregl.NavigationControl({ showZoom: false, showCompass: true  }), 'top-right');
        map.addControl(new maplibregl.FullscreenControl(), 'top-right');
        // GeolocateControl — same options the Android client uses
        // (high-accuracy + track + show-user + accuracy-circle).
        // Renders the operator's GPS fix as a pulsing dot + circle
        // and recentres the map on tap.
        map.addControl(new maplibregl.GeolocateControl({
          positionOptions:    { enableHighAccuracy: true },
          trackUserLocation:  true,
          showUserLocation:   true,
          showAccuracyCircle: true,
        }), 'top-right');
        if (maplibregl.GlobeControl) map.addControl(new maplibregl.GlobeControl(), 'top-right');

        // ─── Bottom-left ─────────────────────────────────────────────────
        map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
        cursorControlRef.current = new CursorCoordinatesControl();
        map.addControl(cursorControlRef.current, 'bottom-left');

        // ─── Terrain DEM ─────────────────────────────────────────────────
        if (mvtApiKey) {
          const DEM_TERRAIN_ID    = 'terrainSource';
          const DEM_HILLSHADE_ID  = 'hillshadeSource';
          const HILLS_LAYER_ID    = 'hills';
          const demUrl = `https://mvt.micro-gis.com/terrain?key=${encodeURIComponent(mvtApiKey)}`;
          window.__copDemConfig = mvtApiKey;

          const ensureDem = () => {
            try {
              if (!map.getSource(DEM_TERRAIN_ID))   map.addSource(DEM_TERRAIN_ID,   { type: 'raster-dem', url: demUrl, tileSize: 256 });
              if (!map.getSource(DEM_HILLSHADE_ID)) map.addSource(DEM_HILLSHADE_ID, { type: 'raster-dem', url: demUrl, tileSize: 256 });
              if (!map.getLayer(HILLS_LAYER_ID)) {
                map.addLayer({
                  id: HILLS_LAYER_ID, type: 'hillshade', source: DEM_HILLSHADE_ID,
                  layout: { visibility: 'none' },
                  paint:  { 'hillshade-shadow-color': '#473B24' },
                });
              }
              const terrainOn = !!(map.getTerrain && map.getTerrain());
              map.setLayoutProperty(HILLS_LAYER_ID, 'visibility', terrainOn ? 'visible' : 'none');
            } catch { /* ignore */ }
          };
          map.on('load', ensureDem);
          map.on('style.load', ensureDem);
          map.addControl(new maplibregl.TerrainControl({ source: DEM_TERRAIN_ID, exaggeration: 1 }), 'top-right');
          map.on('terrain', (e) => {
            const enabled = !!e.terrain;
            if (map.getLayer(HILLS_LAYER_ID))
              map.setLayoutProperty(HILLS_LAYER_ID, 'visibility', enabled ? 'visible' : 'none');
          });
        }

        // ─── Android-parity right-stack tail (2026-05-19) ──────────
        // Same composition + order as tms-map.html:
        //   StyleSwitcher → PitchToggle → Geocoder → MapLanguage →
        //   DayNight → Grid → Rotate → CoordsInfo.
        // Each entry is feature-gated on the global the plugin
        // script exposes, so a missing/failed vendor script just
        // drops that one button instead of blowing up the stack.
        if (window.StyleSwitcherControl)    map.addControl(new window.StyleSwitcherControl(),    'top-right');
        if (window.PitchToggleControl)      map.addControl(new window.PitchToggleControl(),      'top-right');
        if (window.GeocoderControl)         map.addControl(new window.GeocoderControl(),         'top-right');
        if (window.MapLanguageControl)      map.addControl(new window.MapLanguageControl(),      'top-right');
        if (window.DayNightToggleControl)   map.addControl(new window.DayNightToggleControl(),   'top-right');
        if (window.GridToggleControl)       map.addControl(new window.GridToggleControl(),       'top-right');
        if (window.RotateToggleControl)     map.addControl(new window.RotateToggleControl(),     'top-right');
        // Penultimate position — sits between Rotate and CoordsInfo.
        if (window.MinimapToggleControl)    map.addControl(new window.MinimapToggleControl(),    'top-right');
        if (window.CoordsInfoControl)       map.addControl(new window.CoordsInfoControl(),       'top-right');

        // ─── Optional widgets — full mode only ──────────────────────────
        if (mode === 'full') {
          if (window.AwdsWeatherControl) map.addControl(new window.AwdsWeatherControl(), 'top-left');
          if (window.CopLayersControl)   map.addControl(new window.CopLayersControl(),   'top-left');
          // TMS Layers — sits below COP Layers in the top-left stack.
          if (window.TmsLayersControl)   map.addControl(new window.TmsLayersControl(),   'top-left');
          // 2026-06-09 — Air-targets widget. Passive renderer; the
          // SSE subscription is opened right below and re-broadcasts
          // each tick as a window CustomEvent('targets:tick') the
          // widget listens for. See manuals/NODE-targets/PLAN.md.
          if (window.TargetsControl)     map.addControl(new window.TargetsControl(),     'top-left');

          // ─── Air-targets SSE lifecycle ──────────────────────────────
          // Same-origin EventSource → nginx proxies same-VM loopback
          // /api/local/targets/stream on ad-tms-service:6537 (admin
          // plane, no auth, loopback-only bind). Each tick payload
          // shape: { ts, edges, colors } per services/targets-feed.js.
          // The browser's EventSource auto-reconnects with the server-
          // emitted `retry:` interval (2 s in our route), so we don't
          // need our own backoff. Cleanup in useEffect's return will
          // close the socket on component unmount / re-mount.
          try {
            targetsEs = new EventSource(TARGETS_STREAM_URL);
            targetsEs.onmessage = (e) => {
              if (disposed) return;
              try {
                const tick = JSON.parse(e.data);
                window.dispatchEvent(
                  new CustomEvent('targets:tick', { detail: tick }),
                );
              } catch { /* ignore handshake / malformed */ }
            };
            targetsEs.addEventListener('ready', () => {
              // Optional: surface to console for dev verification.
              // No-op in prod; widget waits for first data event.
            });
            targetsEs.onerror = () => {
              // EventSource auto-reconnects; nothing to do here.
              // Logged at debug-ish level only if needed; left silent
              // to avoid console spam during normal reconnects.
            };
          } catch { /* SSE unsupported / nginx 404 — non-fatal */ }
        }

        // ─── Layout polish ───────────────────────────────────────────────
        map.on('load', () => {
          map.setProjection({ type: 'mercator' });

          const bottomLeft = containerRef.current?.querySelector('.maplibregl-ctrl-bottom-left');
          if (bottomLeft instanceof HTMLElement) {
            bottomLeft.style.display = 'flex';
            bottomLeft.style.flexDirection = 'row';
            bottomLeft.style.alignItems = 'center';
            bottomLeft.style.gap = '8px';
            bottomLeft.style.padding = '0 0 10px 10px';
            const scale  = bottomLeft.querySelector('.maplibregl-ctrl-scale');
            const cursor = cursorControlRef.current?._container;
            if (scale  instanceof HTMLElement) { scale.style.order = '1';  scale.style.margin = '0'; }
            if (cursor instanceof HTMLElement) { bottomLeft.appendChild(cursor); }
          }

          window.setTimeout(() => {
            const btn = containerRef.current?.querySelector('.maplibregl-ctrl-attrib-button');
            if (btn instanceof HTMLButtonElement && btn.getAttribute('aria-expanded') !== 'false') btn.click();
          }, 600);

          try { map.resize(); } catch { /* ignore */ }
        });

        if (mode === 'full') {
          if (window.MarkerLabelManager) {
            const labelMgr = new window.MarkerLabelManager(map, { visible: true });
            window.markerLabelManager = labelMgr;
          }
          if (window.MarkerLabelsToggleControl) map.addControl(new window.MarkerLabelsToggleControl(), 'top-left');

          if (window.CopCanvasRenderer && !window.copCanvasRenderer) {
            // Wire the Canvas-badge text→icon override BEFORE constructing
            // CopCanvasRenderer — its constructor synchronously appends
            // the badge via _createDebugBadge(), so the MutationObserver
            // must be registered first to catch the addedNodes mutation.
            setupCanvasBadgeSwap(map);
            window.copCanvasRenderer = new window.CopCanvasRenderer(map, {
              polylines: true,
              polygons:  true,
            });
          }

          // ─── COP-tracks 3D air-track plugin (round 51) ────────────────
          // Wired here AFTER CopCanvasRenderer init (matches the donor
          // cop-map-tab.jsx pattern verbatim). The plugin's monkey-patch
          // hooks CopLayersControl.prototype to react to layer activations
          // — by this point CopLayersControl is BOTH a class on `window`
          // (via app.cop-layers.js script load) AND has at least one
          // instance attached to the map (the addControl above). The
          // plugin's internal retry-loop covers any residual style-load
          // timing, but starting late removes the ES-module evaluation
          // race that a too-early call introduced. Non-fatal if
          // `copTracksInit` is absent (script load failed).
          if (typeof window.copTracksInit === 'function') {
            try { window.copTracksInit(map); }
            catch (e) { console.warn('[cop-tracks] init failed:', e); }
          }
        }

        map.on('mousemove', (e) => {
          cursorControlRef.current?.setValue(`${e.lngLat.lat.toFixed(6)}, ${e.lngLat.lng.toFixed(6)}`);
        });
        map.on('mouseout', () => cursorControlRef.current?.setValue(''));

        mapRef.current = map;
        // 2026-05-19 — Expose the map instance on window.map so the
        // sandbox-vendored plugins (app.daynight.js + app.grid.js)
        // can find it via their `window.map` / `getMap()` lookup.
        // Both plugins were authored for the old monolithic client
        // where the map was a window global; without this assignment
        // their day-night + grid layer toggles silently bail when
        // the operator clicks the toolbar button. CoordsInfo /
        // Geocoder don't need this — they take the map via onAdd(m)
        // closure — but assigning here is harmless for them too.
        window.map = map;
        // 2026-05-19 — Compass "bearing != 0" highlight. The
        // maplibregl.NavigationControl(showCompass:true) draws the
        // bearing needle but doesn't add an .active class to its
        // button when the map rotates — Android's tms-map.html
        // attaches a tiny listener for visual parity (operator
        // request 2026-05-19). Tolerance 0.5° avoids flicker on
        // rounding noise. Looks up the button via querySelector
        // scoped to this map container so multi-map pages don't
        // cross-light each other's compasses.
        (function () {
          var mapEl = map.getContainer();
          function updateCompassActive() {
            var btn = mapEl
                ? mapEl.querySelector('.maplibregl-ctrl-compass')
                : document.querySelector('.maplibregl-ctrl-compass');
            if (!btn) return;
            btn.classList.toggle('active',
                Math.abs(map.getBearing()) > 0.5);
          }
          map.on('rotate',    updateCompassActive);
          map.on('rotateend', updateCompassActive);
          map.on('load',      updateCompassActive);
        })();
        setTimeout(() => { try { map.resize(); } catch { /* ignore */ } }, 0);

        // Notify parent once style finishes — that's when controls/layers are
        // safe to add from outside (e.g. mapbox-gl-draw in the picker modal).
        if (typeof onReady === 'function') {
          if (map.isStyleLoaded?.()) onReady(map, maplibregl);
          else map.once('load', () => onReady(map, maplibregl));
        }
      } catch (err) {
        setErrorMsg(err.message || String(err));
        if (typeof onError === 'function') onError(err);
      }
    }

    mountMap();
    return () => {
      disposed = true;
      // Close air-targets SSE first — its onmessage no-ops on
      // `disposed`, but explicit close releases the socket and
      // stops the browser's auto-reconnect timer.
      if (targetsEs) { try { targetsEs.close(); } catch { /* */ } targetsEs = null; }
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // We deliberately do NOT depend on initialCenter/Zoom — those are
    // initial values only. Re-mount the component to change them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (errorMsg) {
    return (
      <div className={`flex h-full w-full items-center justify-center bg-canvas ${className || ''}`} style={style}>
        <div className="rounded-2xl border border-rose-200 bg-white px-6 py-4 text-sm text-rose-700 shadow-card">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          Map error: {errorMsg}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        flex: '1 1 0',
        alignSelf: 'stretch',
        position: 'relative',
        // Starry-sky backdrop visible through the transparent area
        // around the globe (globe projection + zoomed out past ~z3).
        // MapLibre paints its canvas above this, so the image is
        // hidden whenever the globe fully covers the viewport.
        // Image is tiled at its native size — NOT stretched — so
        // stars stay their natural angular density at any container
        // size. `#000` fills any leftover slack before the tiles
        // paint.
        backgroundImage:    `url(${STARRY_SKY_BG_URL})`,
        backgroundSize:     'auto',
        backgroundRepeat:   'repeat',
        backgroundColor:    '#000',
        ...style,
      }}
    />
  );
}
